import { makeVar } from '@apollo/client'

import { TransformedPaymentRecords } from '../../types'

/**
 * Holds the array of all "local" payments with "Pending" status.
 */
export const temporaryPendingPayments = makeVar<TransformedPaymentRecords[]>([])

/**
 * Holds a reference to the lastest "local" payment with "Pending" status. Necessary
 * because we can't update the onSuccess callback that we pass to rchInit.
 */
export const pendingPaymentToAdd = makeVar<TransformedPaymentRecords | {}>({})

/*
 * Holds a reference to the current payment session id being used in the
 * reach iframe. It's needed because after we finish a payment, if we want
 * to do a new payment without reloading the page we have to force a rerender
 * of the iframe component, which will in turn reinitialize the iframe.
 *
 * Because the iframe registers the `onSuccess` callback we pass to the rchInit
 * function as an event listener to allow communication between our codebase and
 * the iframe, everytime we mount the iframe we register a new onSuccess callback.
 * This scenario will always happen if we want to make a new payment right after
 * the last one we did.
 *
 * By storing the payment session of iframe that we're looking at we can prevent
 * a previous `onSuccess` callback from doing all the things it should do if
 * it doesn't belong to the current payment session that we're storing.
 */
export const iframePaymentSession = makeVar<string>('')

/**
 * Reach's payment method for a bank transfer, as it reaches us: the
 * `SESSION_COMPLETED` webhook's `PaymentMethod` is stored verbatim on the
 * payment record (`payment_method_type`) and surfaces here as `methodName`.
 * Card and PayPal payments carry the card brand instead ("VISA", "AMEX", ...).
 */
export const BANK_TRANSFER_METHOD_NAME = 'BANK'

/*
 * Holds the payment session whose bank transfer instructions are still on
 * screen.
 *
 * Reach's drop-in never fires `onSuccess` for a bank transfer: it completes the
 * session, shows the transfer instructions, and we only hear about it through
 * the `SESSION_COMPLETED` webhook, which creates a PROCESSING payment record.
 * That record counts as paid, so the order's remaining balance drops -- and the
 * page used to react by unmounting the drop-in (full balance) or changing the
 * amount on a session Reach had already completed (partial), either way taking
 * the instructions away before the retailer could copy them (sc-260309).
 *
 * The instructions live inside the iframe and nowhere else, so nothing can
 * bring them back. Until the retailer starts a new payment or leaves the page,
 * whatever the balance now says, the drop-in stays exactly as it is.
 */
export const bankTransferSessionAwaitingInstructions = makeVar<string>('')

/**
 * Whether the drop-in is currently showing bank transfer instructions that the
 * retailer still needs. Compared against the live session so that starting a
 * new payment (which creates a new session) leaves the old one behind.
 */
export const isShowingBankTransferInstructions = () => {
  const session = iframePaymentSession()
  return !!session && bankTransferSessionAwaitingInstructions() === session
}
